import React from 'react';
import { Link } from 'react-router-dom';
import { Skeleton, Tooltip } from '@patternfly/react-core';
import { ClipboardCheckIcon } from '@patternfly/react-icons/dist/esm/icons/clipboard-check-icon';
import { ExclamationTriangleIcon } from '@patternfly/react-icons/dist/esm/icons/exclamation-triangle-icon';
import capitalize from 'lodash-es/capitalize';
import { usePipelinerunActionsLazy } from '~/components/PipelineRun/PipelineRunListView/pipelinerun-actions';
import { PipelineRunTestOutputResult } from '~/components/PipelineRun/PipelineRunListView/PipelineRunTestOutputResult';
import { ScanStatus } from '~/components/PipelineRun/PipelineRunListView/ScanStatus';
import { TriggerColumnData } from '~/components/trigger-column-data/trigger-column-data';
import {
  PipelineRunLabel,
  PipelineRunType,
  runStatus,
  UNFINISHED_PLR_STATUSES,
} from '~/consts/pipelinerun';
import { usePipelineRunTestOutputResult } from '~/hooks/usePipelineRunTestOutputResult';
import { useKarchScanResults } from '~/hooks/useScanResults';
import { COMPONENT_DETAILS_PATH } from '~/routes/paths';
import ActionMenu from '~/shared/components/action-menu/ActionMenu';
import { Duration } from '~/shared/components/duration';
import { ColumnDefinition } from '~/shared/components/TableV2';
import { Timestamp } from '~/shared/components/timestamp/Timestamp';
import { PipelineRunKind } from '~/types';
import { createCommitObjectFromPLR } from '~/utils/commits-utils';
import { getEventTypeLabel } from '~/utils/pipeline-run-filter-utils';
import { getPipelineRunDetailsPath } from '~/utils/pipeline-run-routes';
import { pipelineRunStatus } from '~/utils/pipeline-utils';
import { PLRStatus } from '~/utils/plr-status-config';
import PipelineRunStatusCell from './PipelineRunStatus';

const PipelineRunAttestation: React.FC<{ plr: PipelineRunKind }> = ({ plr }) => {
  const hasAttestation =
    plr.metadata?.annotations?.[PipelineRunLabel.CHAINS_SIGNED_ANNOTATION] === 'true';
  const AttestationIcon = hasAttestation ? ClipboardCheckIcon : ExclamationTriangleIcon;

  return (
    <Tooltip
      content={
        hasAttestation ? 'Pipeline run is signed and attested' : 'Pipeline run is not signed'
      }
    >
      <AttestationIcon
        data-test={hasAttestation ? 'attestation-signed' : 'attestation-unsigned'}
        color={
          hasAttestation
            ? 'var(--pf-t--global--icon--color--status--success--default)'
            : 'var(--pf-t--global--icon--color--status--warning--default)'
        }
        style={{ marginRight: 'var(--pf-t--global--spacer--sm)' }}
      />
    </Tooltip>
  );
};

const shouldShowScanResults = (plr: PipelineRunKind): boolean =>
  plr.metadata?.labels?.[PipelineRunLabel.PIPELINE_TYPE] === PipelineRunType.BUILD &&
  !UNFINISHED_PLR_STATUSES.includes(pipelineRunStatus(plr)) &&
  plr.status?.completionTime !== undefined;

const VulnerabilitiesCell: React.FC<{ plr: PipelineRunKind }> = ({ plr }) => {
  const showScan = shouldShowScanResults(plr);
  const [scanResults, scanLoaded, scanError] = useKarchScanResults(
    showScan ? (plr.metadata?.name ?? '') : '',
  );

  if (scanError) {
    return <>N/A</>;
  }
  if (!showScan || (scanLoaded && !scanResults)) {
    return <>-</>;
  }
  if (scanLoaded) {
    return <ScanStatus scanResults={scanResults} />;
  }
  return <Skeleton />;
};

const TestOutputCell: React.FC<{ plr: PipelineRunKind; namespace: string }> = ({
  plr,
  namespace,
}) => {
  const status = pipelineRunStatus(plr);
  const shouldFetch =
    !!namespace &&
    !UNFINISHED_PLR_STATUSES.includes(status) &&
    plr.status?.completionTime !== undefined;

  const [aggregatedTestResult, isLoading] = usePipelineRunTestOutputResult(
    shouldFetch ? namespace : null,
    plr,
  );

  if (isLoading) {
    return <Skeleton screenreaderText="Loading PipelineRun test output result" />;
  }
  return <PipelineRunTestOutputResult aggregatedTestResult={aggregatedTestResult} />;
};

const ActionCell: React.FC<{ plr: PipelineRunKind }> = ({ plr }) => {
  const [actions, onOpen] = usePipelinerunActionsLazy(plr);
  return <ActionMenu actions={actions} onOpen={onOpen} />;
};

export const plrNameColumn: ColumnDefinition<PipelineRunKind> = {
  id: 'name',
  header: 'Name',
  accessorFn: (row) => row.metadata?.name ?? '',
  pinned: 'start',
  nonHidable: true,
  sortable: true,
  size: 3,
  cell: (info) => {
    const plr = info.row.original;
    const pipelineRunName = plr.metadata?.name ?? '';
    const isFinished = !UNFINISHED_PLR_STATUSES.includes(pipelineRunStatus(plr));
    return (
      <>
        <Link to={getPipelineRunDetailsPath(plr)}>{pipelineRunName}</Link>
        {isFinished && <PipelineRunAttestation plr={plr} />}
      </>
    );
  },
};

export const plrStartedColumn: ColumnDefinition<PipelineRunKind> = {
  id: 'started',
  header: 'Started',
  accessorFn: (row) => row.status?.startTime ?? '',
  sortable: true,
  cell: (info) => (
    <Timestamp timestamp={typeof info.getValue() === 'string' ? (info.getValue() as string) : ''} />
  ),
};

export const plrVulnerabilitiesColumn: ColumnDefinition<PipelineRunKind> = {
  id: 'vulnerabilities',
  header: 'Vulnerabilities',
  visibleFrom: 'xl',
  cell: (info) => <VulnerabilitiesCell plr={info.row.original} />,
};

export const plrDurationColumn: ColumnDefinition<PipelineRunKind> = {
  id: 'duration',
  header: 'Duration',
  accessorFn: (row) => row.status?.startTime ?? '',
  sortable: true,
  visibleFrom: 'xl',
  cell: (info) => {
    const plr = info.row.original;
    const status = pipelineRunStatus(plr);
    if (status === runStatus.Pending) {
      return <>-</>;
    }
    return (
      <Duration
        startTime={typeof plr.status?.startTime === 'string' ? plr.status.startTime : undefined}
        endTime={
          typeof plr.status?.completionTime === 'string' ? plr.status.completionTime : undefined
        }
      />
    );
  },
};

export const plrStatusColumn: ColumnDefinition<PipelineRunKind> = {
  id: 'status',
  header: 'Status',
  accessorFn: (row) => PLRStatus.registry.deriveStatus(row),
  sortable: true,
  cell: (info) => <PipelineRunStatusCell plr={info.row.original} />,
};

export const plrTestOutputColumn: ColumnDefinition<PipelineRunKind> = {
  id: 'testOutput',
  header: 'Test output',
  visibleFrom: 'xl',
  cell: (info) => {
    const plr = info.row.original;
    const namespace = plr.metadata?.namespace ?? '';
    return <TestOutputCell plr={plr} namespace={namespace} />;
  },
};

export const plrTypeColumn: ColumnDefinition<PipelineRunKind> = {
  id: 'type',
  header: 'Type',
  accessorFn: (row) => row.metadata?.labels?.[PipelineRunLabel.PIPELINE_TYPE] ?? '',
  sortable: true,
  visibleFrom: 'xl',
  cell: (info) => <>{capitalize(info.getValue() as string) || '-'}</>,
};

export const plrComponentColumn: ColumnDefinition<PipelineRunKind> = {
  id: 'component',
  header: 'Component',
  accessorFn: (row) => row.metadata?.labels?.[PipelineRunLabel.COMPONENT] ?? '',
  sortable: true,
  visibleFrom: 'xl',
  cell: (info) => {
    const plr = info.row.original;
    const componentName = plr.metadata?.labels?.[PipelineRunLabel.COMPONENT];
    const applicationName = plr.metadata?.labels?.[PipelineRunLabel.APPLICATION];
    const namespace = plr.metadata?.namespace ?? '';
    if (!componentName) {
      return <>-</>;
    }
    if (!applicationName) {
      return <>{componentName}</>;
    }
    return (
      <Link
        to={COMPONENT_DETAILS_PATH.createPath({
          workspaceName: namespace,
          applicationName,
          componentName,
        })}
      >
        {componentName}
      </Link>
    );
  },
};

export const plrTriggerReferenceColumn: ColumnDefinition<PipelineRunKind> = {
  id: 'triggerReference',
  header: 'Trigger / Reference',
  visibleFrom: 'xl',
  size: 2,
  cell: (info) => {
    const plr = info.row.original;
    const commit = createCommitObjectFromPLR(plr);
    if (!commit) {
      return <>-</>;
    }
    const eventTypeLabel = getEventTypeLabel(commit.eventType, commit.gitProvider);
    return (
      <>
        {eventTypeLabel}{' '}
        <TriggerColumnData
          repoURL={commit.repoURL}
          prNumber={commit.pullRequestNumber}
          eventType={commit.eventType}
          commitSha={commit.sha}
          shaUrl={commit.shaURL}
          gitProvider={commit.gitProvider}
        />
      </>
    );
  },
};

export const plrActionsColumn: ColumnDefinition<PipelineRunKind> = {
  id: 'actions',
  header: '',
  pinned: 'end',
  nonHidable: true,
  width: '48px',
  cell: (info) => <ActionCell plr={info.row.original} />,
};

export const pipelineRunsColumns: ColumnDefinition<PipelineRunKind>[] = [
  plrNameColumn,
  plrStartedColumn,
  plrVulnerabilitiesColumn,
  plrDurationColumn,
  plrStatusColumn,
  plrTestOutputColumn,
  plrTypeColumn,
  plrComponentColumn,
  plrTriggerReferenceColumn,
  plrActionsColumn,
];
